import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Observable, catchError, map, of } from 'rxjs';
import { LocalCacheService } from '../services/local-cache.service';
import { TokenRefreshService } from '../services/token-refresh.service';

/**
 * The access token has expired but Console holds its own refresh token (for example the admin reloads the page
 * after lunch): refresh once, then let the route through. Returns null when there is nothing to refresh with.
 * Must run in an injection context. Shared by authGuard and roleGuard; both share the one refresh call.
 */
export function refreshExpiredSession(): Observable<boolean> | null {
  const cache = inject(LocalCacheService);
  if (cache.isAuthenticated || !cache.refreshToken) {
    return null;
  }
  return inject(TokenRefreshService)
    .refresh()
    .pipe(
      map(() => true),
      catchError(() => of(false)),
    );
}

export const authGuard: CanActivateFn = () => {
  const cache = inject(LocalCacheService);
  const router = inject(Router);

  if (cache.isAuthenticated) {
    return true;
  }

  // Navigate to clean sign-in URL without any query params (removes deviceId from URL)
  const signIn: UrlTree = router.createUrlTree(['/sign-in']);
  const refreshed = refreshExpiredSession();
  return refreshed ? refreshed.pipe(map((ok) => (ok ? true : signIn))) : signIn;
};
